import React, { useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import translations from '../../translation'; // Import translations file

// Register chart.js components
ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, LinearScale);

const Chart = ({ content, language }) => {
  const translate = (key) => translations[language]?.[key] || key;

  // Extracting chart data
  const chartData = {
    labels: [
      translate('approvedLabel'),
      translate('pendingLabel'),
      translate('rejectedLabel'),
    ],
    datasets: [
      {
        data: [content.approved, content.pending, content.rejected],
        backgroundColor: ['#16a34a', '#4c82e7', '#dc2626'],
        hoverBackgroundColor: ['#4caf50', '#1e90ff', '#f87171'],
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.raw}`;
          },
        },
      },
    },
  };

  // State for toggling data dynamically
  const [selectedStatuses, setSelectedStatuses] = useState({
    approved: true,
    pending: true,
    rejected: true,
  });

  const handleStatusToggle = (status) => {
    setSelectedStatuses((prevState) => ({
      ...prevState,
      [status]: !prevState[status],
    }));
  };

  // Filtered data based on toggles
  const filteredData = {
    labels: [
      translate('approvedLabel'),
      translate('pendingLabel'),
      translate('rejectedLabel'),
    ],
    datasets: [
      {
        data: [
          selectedStatuses.approved ? content.approved : 0,
          selectedStatuses.pending ? content.pending : 0,
          selectedStatuses.rejected ? content.rejected : 0,
        ],
        backgroundColor: ['#16a34a', '#4c82e7', '#dc2626'],
        hoverBackgroundColor: ['#4caf50', '#1e90ff', '#f87171'],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="max-w-full w-full bg-white rounded-lg mx-auto p-4 md:p-6">
      <div className="flex justify-between mb-3">
        {/* Title Section */}
        <h5 className="text-xl font-bold leading-none text-black">
          {translate('statusDistributionTitle')}
        </h5>
      </div>

      {/* Toggle Switches for Status */}
      <div className="flex mb-4">
        <div className="flex items-center mr-4">
          <input
            id="approved"
            type="checkbox"
            checked={selectedStatuses.approved}
            onChange={() => handleStatusToggle('approved')}
            className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
          />
          <label htmlFor="approved" className="ml-2 text-sm font-medium text-black">
            {translate('approvedLabel')}
          </label>
        </div>
        <div className="flex items-center mr-4">
          <input
            id="pending"
            type="checkbox"
            checked={selectedStatuses.pending}
            onChange={() => handleStatusToggle('pending')}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="pending" className="ml-2 text-sm font-medium text-black">
            {translate('pendingLabel')}
          </label>
        </div>
        <div className="flex items-center">
          <input
            id="rejected"
            type="checkbox"
            checked={selectedStatuses.rejected}
            onChange={() => handleStatusToggle('rejected')}
            className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
          />
          <label htmlFor="rejected" className="ml-2 text-sm font-medium text-black">
            {translate('rejectedLabel')}
          </label>
        </div>
      </div>

      {/* Donut Chart */}
      <div className="py-6" style={{ height: '400px' }}>
        <Doughnut data={filteredData} options={options} />
      </div>
    </div>
  );
};

export default Chart;
